/**
 * Catalogo aggregato delle mosse di tutte le classi DWR trascritte.
 *
 * Esposto come `Record<ClassId, Move[]>` per lookup veloce dato il
 * `classId` di un PG. Non include le mosse `base` o `system` (che
 * vivranno in `src/data/moves/base/` quando saranno trascritte).
 */

import type { ClassId } from '../../schemas/classId';
import type { Move } from '../../schemas/move';
import { barbaroMoves } from './barbaro';
import { bardoMoves } from './bardo';
import { chiericoMoves } from './chierico';
import { commonMoves, findCommonMoveById } from './common';
import { druidoMoves } from './druido';
import { guerrieroMoves } from './guerriero';
import { ladroMoves } from './ladro';
import { magoMoves } from './mago';
import { monacoMoves } from './monaco';
import { paladinoMoves } from './paladino';
import { rangerMoves } from './ranger';
import { sciamanoMoves } from './sciamano';
import { warlockMoves } from './warlock';

export {
  barbaroMoves,
  bardoMoves,
  chiericoMoves,
  commonMoves,
  druidoMoves,
  findCommonMoveById,
  guerrieroMoves,
  ladroMoves,
  magoMoves,
  monacoMoves,
  paladinoMoves,
  rangerMoves,
  sciamanoMoves,
  warlockMoves,
};

/**
 * Lookup completo: tutte e **12** le classi DWR sono ora presenti
 * (Fase C COMPLETATA).
 *
 * Il tipo resta `Partial<Record<ClassId, readonly Move[]>>` per coerenza con
 * il pattern dei consumer esistenti (controllo `if (!catalog) return
 * undefined` in `findMoveById`). Upgrade a `Record` strict possibile in Fase D.
 */
export const movesByClass: Partial<Record<ClassId, readonly Move[]>> = {
  barbaro: barbaroMoves,
  bardo: bardoMoves,
  chierico: chiericoMoves,
  druido: druidoMoves,
  guerriero: guerrieroMoves,
  ladro: ladroMoves,
  mago: magoMoves,
  monaco: monacoMoves,
  paladino: paladinoMoves,
  ranger: rangerMoves,
  sciamano: sciamanoMoves,
  warlock: warlockMoves,
};

/**
 * Cerca una mossa per ID nel catalogo di una specifica classe.
 * Restituisce `undefined` se la classe non è nel catalogo o l'ID non esiste.
 */
export function findMoveById(classId: ClassId, moveId: string): Move | undefined {
  const catalog = movesByClass[classId];
  if (!catalog) return undefined;
  return catalog.find((m) => m.id === moveId);
}

/**
 * Cerca una mossa per ID **considerando anche le mosse comuni** (es.
 * *Infanzia Complessa*). Utile per il LevelUpFlow (D1) dove le common
 * appaiono nel pool di scelta accanto alle advanced della classe.
 *
 * Lookup: prima nelle mosse della classe specifica, poi nel catalogo
 * comune (`common.*`). Restituisce `undefined` solo se l'ID non esiste
 * in nessuno dei due cataloghi.
 */
export function findMoveByIdAnyScope(classId: ClassId, moveId: string): Move | undefined {
  return findMoveById(classId, moveId) ?? findCommonMoveById(moveId);
}

/**
 * **FI-30 (D6)**: cerca una mossa per ID in **tutte** le classi del
 * catalogo (oltre alle comuni). Necessario quando una mossa concede al
 * PG una mossa di un'altra classe (`MoveSchema.grantsMoveChoice` con
 * `kind: 'from-choice-mapping'`).
 *
 * Lookup: itera tutte le 12 classi in `movesByClass`, fallback a
 * `findCommonMoveById`. Restituisce `undefined` solo se l'ID non esiste
 * in nessun catalogo.
 *
 * Esempio: per un PG Barbaro che acquisisce `ladro.io-sparo-per-primo`
 * via *Essere il Totem*, `findMoveByIdAnyScope('barbaro', ...)` ritorna
 * `undefined` (la mossa non è nel catalogo Barbaro), mentre
 * `findMoveByIdGlobal('ladro.io-sparo-per-primo')` la trova.
 *
 * **Quando usarla** vs `findMoveByIdAnyScope`:
 *  - `findMoveByIdAnyScope`: la mossa attesa è della classe del PG (o
 *    comune). Caso normale del LevelUpFlow per la mossa principale.
 *  - `findMoveByIdGlobal`: la mossa può essere di **qualunque** classe.
 *    Caso `'granted'` cross-class (FI-30 oggi, FI-17 D7 in futuro).
 */
export function findMoveByIdGlobal(moveId: string): Move | undefined {
  for (const catalog of Object.values(movesByClass)) {
    if (!catalog) continue;
    const found = catalog.find((m) => m.id === moveId);
    if (found) return found;
  }
  return findCommonMoveById(moveId);
}
